// src/components/auth/registration/CategoriesStep.jsx - Updated for existing security rules
import React, { useState, useEffect, useCallback } from 'react';
import Button from '../../common/Button';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { useAuth } from '../../../contexts/AuthContext';

const CategoriesStep = ({ data, updateData, nextStep, prevStep }) => {
  const { localCategories, addLocalCategory, removeLocalCategory } = useAuth();
  const [systemCategories, setSystemCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState(data.categories || []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Add category modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingCategory, setAddingCategory] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    imageUrl: ''
  });

  // Combine system and local categories
  const allCategories = [...systemCategories, ...localCategories];

  // Fetch system categories from Firestore (excluding user-created ones)
  useEffect(() => {
    const fetchSystemCategories = async () => {
      try {
        const categoriesRef = collection(db, 'categories');
        const snapshot = await getDocs(categoriesRef);

        const categoriesList = snapshot.docs
          .map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              name: data.name || formatCategoryName(doc.id),
              description: data.description || `${formatCategoryName(doc.id)} components and parts`,
              image: data.image || 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=400&h=300&fit=crop',
              isActive: data.isActive !== false,
              order: data.order || 0,
              isGlobal: true,
              isUserCreated: data.isUserCreated || false,
              createdBy: data.createdBy,
              ...data
            };
          })
          // Filter out user-created categories (only show system categories)
          .filter(category => !category.isUserCreated);

        // Sort categories by order field, then by name
        categoriesList.sort((a, b) => {
          if (a.order !== b.order) {
            return a.order - b.order;
          }
          return a.name.localeCompare(b.name);
        });

        setSystemCategories(categoriesList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching system categories:", error);
        setError('Failed to load categories from database. Using local categories only.');
        setLoading(false);
      }
    };

    fetchSystemCategories();
  }, []);

  // Function to format category ID to display name
  const formatCategoryName = (id) => {
    return id.split('-').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Handle adding new category locally
  const handleAddCategory = useCallback(async () => {
    const name = formData.name.trim();
    const description = formData.description.trim();

    if (!name) {
      setError('Category name is required');
      return;
    }

    setAddingCategory(true);
    setError('');

    try {
      // Check if category already exists (both system and local)
      const existingCategory = allCategories.find(cat =>
        cat.name.toLowerCase() === name.toLowerCase()
      );

      if (existingCategory) {
        setError('A category with this name already exists');
        setAddingCategory(false);
        return;
      }

      const categoryData = {
        name: name,
        description: description || `${name} components and parts`,
        image: imageFile || formData.imageUrl || 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=400&h=300&fit=crop'
      };

      // Add to local storage
      const newCategory = await addLocalCategory(categoryData);

      // Reset form and close modal
      handleCloseModal();

      // Show success message temporarily
      setError(`✅ Category "${categoryData.name}" added locally! It will be saved when you complete registration.`);
      setTimeout(() => {
        setError('');
      }, 3000);

    } catch (error) {
      console.error("Error adding category:", error);
      setError('Failed to add category. Please try again.');
    } finally {
      setAddingCategory(false);
    }
  }, [formData, allCategories, imageFile, addLocalCategory]);

  // Handle input changes
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  // Handle image file change
  const handleImageFileChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setError('Image file size should be less than 5MB');
        return;
      }

      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);

      // Clear URL input when file is selected
      setFormData(prev => ({ ...prev, imageUrl: '' }));
    }
  }, []);

  // Handle image URL change
  const handleImageUrlChange = useCallback((e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, imageUrl: value }));

    if (value && !imageFile) {
      setImagePreview(value);
    } else if (!value) {
      setImagePreview('');
    }
  }, [imageFile]);

  // Clear file selection
  const clearFileSelection = useCallback(() => {
    setImageFile(null);
    setImagePreview('');
    const fileInput = document.getElementById('imageFileInput');
    if (fileInput) fileInput.value = '';

    // Show URL preview if URL exists
    if (formData.imageUrl) {
      setImagePreview(formData.imageUrl);
    }
  }, [formData.imageUrl]);

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    setShowAddModal(false);
    setImageFile(null);
    setImagePreview('');
    setError('');
    setFormData({ name: '', description: '', imageUrl: '' });

    // Clear file input
    const fileInput = document.getElementById('imageFileInput');
    if (fileInput) fileInput.value = '';
  }, []);

  // Toggle category selection
  const toggleCategory = useCallback((categoryId) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  }, []);

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (selectedCategories.length === 0) {
      setError('Please select at least one category to continue');
      return;
    }

    updateData({
      categories: selectedCategories,
      categoryObjects: allCategories.filter(cat => selectedCategories.includes(cat.id))
    });
    nextStep();
  };

  // Enhanced Category Card
  const CategoryCard = React.memo(({ category }) => {
    const isSelected = selectedCategories.includes(category.id);
    const isLocal = category.isLocal;

    return (
      <div
        className={`group relative overflow-hidden rounded-xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-105 ${
          isSelected
            ? 'border-orange-500 shadow-xl ring-4 ring-orange-200'
            : 'border-gray-200 hover:border-orange-300 hover:shadow-lg'
        }`}
        onClick={() => toggleCategory(category.id)}
      >
 
     

        {/* Checkbox */}
        <div className="absolute top-3 right-3 z-10">
          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shadow-lg transition-all duration-200 ${
            isSelected
              ? 'bg-orange-500 border-orange-500 scale-110'
              : 'bg-white border-gray-300 group-hover:border-orange-400'
          }`}>
            {isSelected && (
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </div>
        </div>

        {/* Category Image */}
        <div className="relative h-48 w-full bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
          <img
            src={category.image}
            alt={category.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=400&h=300&fit=crop';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        </div>

        {/* Category Info */}
        <div className="p-4 bg-white">
          <h4 className="font-semibold text-gray-900 text-base mb-1 line-clamp-2">
            {category.name}
          </h4>
          {category.description && (
            <p className="text-sm text-gray-600 line-clamp-2">
              {category.description}
            </p>
          )}
        </div>

        {/* Selection indicator */}
        {isSelected && (
          <div className="absolute inset-0 bg-orange-500/10 pointer-events-none">
            <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-orange-500 to-orange-600"></div>
          </div>
        )}

      
      </div>
    );
  });

  return (
    <div className="mt-8 space-y-6">
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              Select Product Categories
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Choose the categories of automobile spare parts that you deal in. You can select multiple categories.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors duration-200"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Category
          </button>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-orange-600 mb-4"></div>
            <p className="text-gray-600">Loading categories...</p>
          </div>
        ) : (
          <>
            {error && (
              <div className={`mb-6 p-4 text-sm border rounded-lg flex items-start ${
                error.includes('✅')
                  ? 'text-green-800 bg-green-100 border-green-200'
                  : 'text-amber-800 bg-amber-100 border-amber-200'
              }`}>
                <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  {error.includes('✅') ? (
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  ) : (
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  )}
                </svg>
                {error}
              </div>
            )}

            {/* Search and Filter Bar */}
            <div className="mb-6 flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search categories..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <span className="font-medium">Selected:</span>
                <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full font-semibold">
                  {selectedCategories.length}
                </span>
                <span>of {allCategories.filter(cat =>
                  cat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  (cat.description && cat.description.toLowerCase().includes(searchTerm.toLowerCase()))
                ).length}</span>
              </div>
            </div>

            {/* Categories Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {allCategories
                .filter(cat =>
                  cat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  (cat.description && cat.description.toLowerCase().includes(searchTerm.toLowerCase()))
                )
                .map(category => (
                  <CategoryCard key={category.id} category={category} />
                ))}
            </div>

            {/* No Results Message */}
            {allCategories.filter(cat =>
              cat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              (cat.description && cat.description.toLowerCase().includes(searchTerm.toLowerCase()))
            ).length === 0 && searchTerm && (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.467-.881-6.072-2.33M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  <h3 className="mt-4 text-sm font-medium text-gray-900">No categories found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Try adjusting your search terms or add a new category.
                  </p>
                </div>
              )}

            {/* Selected Categories Summary */}
            {selectedCategories.length > 0 && (
              <div className="mt-8 p-6 bg-orange-50 border border-orange-200 rounded-xl">
                <h4 className="text-lg font-medium text-orange-900 mb-3">
                  Selected Categories ({selectedCategories.length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {selectedCategories.map(categoryId => {
                    const category = allCategories.find(cat => cat.id === categoryId);
                    return category ? (
                      <span
                        key={categoryId}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800 border border-orange-200"
                      >
                        {category.name}
                    
                        <button
                          type="button"
                          onClick={() => toggleCategory(categoryId)}
                          className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full bg-orange-200 hover:bg-orange-300 focus:outline-none"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {/* Helper Text */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                💡 Tip: Select all relevant categories to help customers find your products more easily
              </p>
            </div>
          </>
        )}
      </div>

      {/* Action Buttons */}
      <form onSubmit={handleSubmit}>
        <div className="flex justify-between items-center pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={prevStep}
            className="px-6 py-2"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </Button>

          <Button
            type="submit"
            variant="primary"
            disabled={loading || selectedCategories.length === 0}
            className="px-8 py-2"
          >
            Next Step
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Button>
        </div>
      </form>

      {/* Add Category Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" 
              onClick={handleCloseModal}
            ></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Add New Category
                    </h3>
                    
                    <div className="space-y-4">
                      {/* Category Name Input */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Category Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="Enter category name"
                          autoComplete="off"
                        />
                      </div>
                      
                      {/* Description Input */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description (Optional)
                        </label>
                        <textarea
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="Enter category description"
                          rows={3}
                          autoComplete="off"
                        />
                      </div>

                      {/* Image Options */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Category Image
                        </label>
                        
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm text-gray-600 mb-1">
                              Upload Image File
                            </label>
                            <div className="flex items-center space-x-2">
                              <input
                                type="file"
                                id="imageFileInput"
                                accept="image/*"
                                onChange={handleImageFileChange}
                                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                              />
                              {imageFile && (
                                <button
                                  type="button"
                                  onClick={clearFileSelection}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>

                          <div className="text-center text-gray-500 text-sm">OR</div>

                          <div>
                            <label className="block text-sm text-gray-600 mb-1">
                              Image URL
                            </label>
                            <input
                              type="url"
                              name="imageUrl"
                              value={formData.imageUrl}
                              onChange={handleImageUrlChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              placeholder="https://example.com/image.jpg"
                              autoComplete="off"
                              disabled={!!imageFile}
                            />
                          </div>
                        </div>
                        
                        {/* Image Preview */}
                        {imagePreview && (
                          <div className="mt-3">
                            <p className="text-xs text-gray-600 mb-2">Image Preview:</p>
                            <img
                              src={imagePreview}
                              alt="Category preview"
                              className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                              onError={(e) => {
                                e.target.src = 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=400&h=300&fit=crop';
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                   
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleAddCategory}
                  disabled={addingCategory || uploadingImage || !formData.name.trim()}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-orange-600 text-base font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addingCategory || uploadingImage ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {uploadingImage ? 'Uploading...' : 'Adding...'}
                    </>
                  ) : (
                    'Add Category'
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoriesStep;